// AIDEV-NOTE: Escuta mudancas no status do pedido via Supabase Realtime
// Atualiza automaticamente o TanStack Query cache quando status muda

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../services/supabase.client';
import type { RealtimePostgresUpdatePayload } from '@supabase/supabase-js';

// AIDEV-NOTE: Tipo parcial representando os campos relevantes da tabela pedidos
// que podem chegar via Realtime UPDATE
interface PedidoRealtimePayload {
  id: string;
  status: string;
  sync_error: string | null;
  numero: number | null;
  updated_at: string;
}

/**
 * Hook que se inscreve no canal Supabase Realtime para um pedido especifico.
 * Quando o status do pedido muda (ex: sincronizando -> sincronizado),
 * invalida a query do TanStack Query para forcar refetch.
 *
 * @param pedidoId - UUID do pedido a observar. Se vazio/undefined, nao se inscreve.
 */
export function usePedidoRealtime(pedidoId: string | undefined): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!pedidoId) return;

    // AIDEV-NOTE: Canal unico por pedido para evitar receber eventos irrelevantes
    const channelName = `pedido-status-${pedidoId}`;

    const channel = supabase
      .channel(channelName)
      .on<PedidoRealtimePayload>(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'pedidos',
          filter: `id=eq.${pedidoId}`
        },
        (payload: RealtimePostgresUpdatePayload<PedidoRealtimePayload>) => {
          // AIDEV-NOTE: Invalida queries relacionadas ao pedido para forcar refetch
          // Isso garante que o UI reflita o novo status imediatamente
          void queryClient.invalidateQueries({ queryKey: ['pedido', pedidoId] });
          void queryClient.invalidateQueries({ queryKey: ['pedidos'] });

          console.log(`[Realtime] Pedido ${pedidoId} atualizado:`, payload.new.status);
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          console.warn(`[Realtime] Erro no canal ${channelName}`);
        }
      });

    // Cleanup: remove subscription ao desmontar ou quando pedidoId muda
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [pedidoId, queryClient]);
}
